/**
 * Focus survival across destroy-and-rebuild repaints.
 *
 * Many of our menu screens (`paint(root, ctx)` style) regenerate their entire
 * subtree when a value changes — a click on a CycleBadge / SidePicker swatch
 * fires `onChange`, the consumer repaints, the old focused node is gone, and
 * focus snaps back to `<body>`. Keyboard / screen-reader users lose their
 * place and have to Tab from the top.
 *
 * Pattern:
 *
 *   const restore = captureFocus();
 *   paint(...);            // destroys + rebuilds DOM
 *   restore();             // re-focuses the equivalent element if it exists
 *
 * For components that participate in this, stamp the *outer* element with a
 * stable `data-focus-key` and (optionally) tag specific inner controls with
 * one of the recognised sub-target attributes (see `SUB_TARGET_ATTRS`). The
 * helper restores focus on:
 *
 *   1. the inner control matching the sub-target, if the capture was on one
 *   2. otherwise the outer `[data-focus-key]` element itself (if focusable)
 *
 * `withFocusPreserved(cb)` is sugar for `const r = capture(); cb(); r()`.
 */

/**
 * Attributes that identify a focusable sub-target inside a `data-focus-key`
 * container. Extend this list when a new component needs sub-target
 * preservation. Order doesn't matter — the helper picks whichever the focused
 * element carries.
 */
const SUB_TARGET_ATTRS = ["data-selector-arrow", "data-side-value"] as const;

type SubTarget = { readonly attr: string; readonly value: string };

interface Capture {
  readonly focusKey: string;
  readonly subTarget: SubTarget | null;
}

function readCapture(root: ParentNode): Capture | null {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement)) return null;
  // Restrict to the consumer's root so unrelated focus elsewhere on the page
  // doesn't trigger a phantom restore.
  if (root instanceof HTMLElement && !root.contains(active) && root !== active) {
    return null;
  }
  const keyEl = active.closest<HTMLElement>("[data-focus-key]");
  if (keyEl === null) return null;
  const focusKey = keyEl.dataset.focusKey;
  if (focusKey === undefined || focusKey === "") return null;
  let subTarget: SubTarget | null = null;
  if (active !== keyEl) {
    for (const attr of SUB_TARGET_ATTRS) {
      const value = active.getAttribute(attr);
      if (value !== null) {
        subTarget = { attr, value };
        break;
      }
    }
  }
  return { focusKey, subTarget };
}

function attrEscape(value: string): string {
  // Escape `\` and `"` so the value can be embedded in a double-quoted
  // attribute selector. Sufficient for CSS attribute matching; ids would need
  // wider escaping but we never restore by id here.
  return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

function applyRestore(capture: Capture, root: ParentNode): void {
  const container = root.querySelector<HTMLElement>(
    `[data-focus-key="${attrEscape(capture.focusKey)}"]`,
  );
  if (container === null) return;
  if (capture.subTarget !== null) {
    const sub = container.querySelector<HTMLElement>(
      `[${capture.subTarget.attr}="${attrEscape(capture.subTarget.value)}"]`,
    );
    if (sub !== null) {
      sub.focus();
      return;
    }
  }
  if (container.tabIndex !== -1) container.focus();
}

/**
 * Snapshot the currently-focused element's position within the focus-key
 * grid. Returns a `restore()` thunk; calling it after the DOM rebuild
 * re-focuses the equivalent element if one exists. Both halves no-op
 * gracefully when nothing tracked was focused.
 */
export function captureFocus(root: ParentNode = document): () => void {
  const capture = readCapture(root);
  if (capture === null) return () => {};
  return () => applyRestore(capture, root);
}

/**
 * Run a repaint callback with focus preservation wrapped around it.
 * Convenience for the common synchronous case.
 */
export function withFocusPreserved(repaint: () => void, root: ParentNode = document): void {
  const restore = captureFocus(root);
  repaint();
  restore();
}
